/**
 * Luxire Wishlist (3V3) — UI controller.
 *
 * Storage is handled by LuxireStorage (luxire-storage.js), which requires login
 * and stores items server-side in a Shopify metafield via the /apps/luxire-api proxy.
 *
 * What this file does:
 *   • Event-delegated click handler for any [data-wishlist-btn] in the DOM
 *     (works for buttons rendered now, later, or via AJAX).
 *   • Visual state sync on every button (is-wishlisted class + aria-label).
 *   • Header count badge sync (any [data-wishlist-count] element).
 *   • Wishlist grid rendering when a #wishlist-grid container exists
 *     (used on the account page's wishlist tab).
 *
 * Logged-out behaviour: clicking any heart calls LuxireStorage.addToWishlist(),
 * which triggers requireLogin() → redirect to /account/login. That is the
 * intentional product flow for the 3V3 theme.
 *
 * Public API: window.LuxireWishlist = { refresh, toggle, isIn, render }
 */
(function() {
  'use strict';

  // -------------------------------------------------------------------------
  // Item shape & helpers
  // -------------------------------------------------------------------------

  function normalizeItem(item) {
    if (!item) return null;
    if (typeof item === 'string') {
      return { handle: item, title: '', image: '', price: '', url: '/products/' + item, variantId: null, type: '' };
    }
    if (typeof item !== 'object') return null;
    var handle = item.handle || item.product_handle || item.productHandle || '';
    if (!handle && (item.url || item.product_url)) {
      var m = String(item.url || item.product_url).match(/\/products\/([^?#\/]+)/);
      if (m) handle = m[1];
    }
    return {
      handle: handle,
      title: item.title || item.product_title || '',
      image: item.image || item.featured_image || item.product_image || '',
      price: item.price || item.product_price || '',
      url: item.url || item.product_url || (handle ? '/products/' + handle : '#'),
      variantId: item.variantId || item.variant_id || null,
      type: item.type || item.product_type || ''
    };
  }

  // Map a raw Shopify product.type into one of our wishlist filter buckets.
  // Order matters — check "suit" before "jacket" (suit jackets), "outerwear"
  // before "jacket" (overcoats), "jacket" before "shirt" (shirt jackets).
  var CATEGORY_LABELS = {
    shirts:    { label: 'Shirts',    icon: 'shirt' },
    trousers:  { label: 'Trousers',  icon: 'trousers' },
    jackets:   { label: 'Jackets',   icon: 'jacket' },
    suits:     { label: 'Suits',     icon: 'suit' },
    outerwear: { label: 'Outerwear', icon: 'coat' },
    other:     { label: 'Other',     icon: 'package' }
  };
  // Pill icons from the shared icon set (assets/lx-icons.js); empty if that script is missing.
  function pillIcon(name) { return (window.LxIcon && window.LxIcon(name, 15)) || ''; }
  function categorize(type) {
    var t = String(type || '').toLowerCase();
    if (!t) return 'other';
    if (t.indexOf('suit') >= 0) return 'suits';
    if (t.indexOf('outerwear') >= 0 || t.indexOf('overcoat') >= 0 || t.indexOf('coat') >= 0) return 'outerwear';
    if (t.indexOf('jacket') >= 0 || t.indexOf('blazer') >= 0) return 'jackets';
    if (t.indexOf('shirt') >= 0 || t.indexOf('polo') >= 0) return 'shirts';
    if (t.indexOf('pant') >= 0 || t.indexOf('trouser') >= 0 || t.indexOf('jean') >= 0 || t.indexOf('chino') >= 0 || t.indexOf('short') >= 0) return 'trousers';
    return 'other';
  }

  // Storage may not be loaded yet (script ordering) — guard every access.
  function storageReady() {
    return typeof LuxireStorage !== 'undefined';
  }

  function isLoggedIn() {
    return storageReady() && LuxireStorage.isLoggedIn();
  }

  function cachedList() {
    if (!storageReady()) return [];
    var raw = LuxireStorage.getWishlistSync() || [];
    return Array.isArray(raw) ? raw.map(normalizeItem).filter(Boolean) : [];
  }

  function isInWishlist(handle) {
    return storageReady() ? LuxireStorage.isWishlisted(handle) : false;
  }

  // -------------------------------------------------------------------------
  // Button + count visual sync
  // -------------------------------------------------------------------------

  function updateAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(function(btn) {
      var handle = btn.getAttribute('data-wishlist-handle');
      if (isInWishlist(handle)) {
        btn.classList.add('is-wishlisted');
        btn.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        btn.classList.remove('is-wishlisted');
        btn.setAttribute('aria-label', 'Add to wishlist');
      }
    });
  }

  function updateCount() {
    var count = cachedList().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function(el) {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  // -------------------------------------------------------------------------
  // Toggle (single button click)
  // -------------------------------------------------------------------------

  function toggleFromButton(btn) {
    if (!storageReady()) return;
    var handle = btn.getAttribute('data-wishlist-handle');
    if (!handle) return;

    var alreadyIn = isInWishlist(handle);
    btn.disabled = true;
    btn.classList.add('is-busy');

    var op;
    if (alreadyIn) {
      op = LuxireStorage.removeFromWishlist(handle);
    } else {
      var item = {
        handle: handle,
        title: btn.getAttribute('data-wishlist-title') || '',
        image: btn.getAttribute('data-wishlist-image') || '',
        price: btn.getAttribute('data-wishlist-price') || '',
        url: btn.getAttribute('data-wishlist-url') || ('/products/' + handle),
        type: btn.getAttribute('data-wishlist-type') || ''
      };
      op = LuxireStorage.addToWishlist(item);
    }

    Promise.resolve(op).then(function() {
      updateAllButtons();
      updateCount();
      // If we're on the account page wishlist tab, refresh its grid too.
      if (document.getElementById('wishlist-grid')) {
        renderWishlistGrid();
      }
    }).catch(function(e) {
      console.warn('[LuxireWishlist] toggle failed', e);
    }).finally(function() {
      btn.disabled = false;
      btn.classList.remove('is-busy');
    });
  }

  // -------------------------------------------------------------------------
  // Grid renderer (account page tab)
  // -------------------------------------------------------------------------

  function enrichMissing(items) {
    // For items missing title/image/type, fetch /products/{handle}.js to fill in.
    var needs = items.filter(function(it) {
      return it.handle && (!it.title || !it.image || !it.type);
    });
    if (needs.length === 0) return Promise.resolve(items);
    return Promise.all(needs.map(function(it) {
      return fetch('/products/' + encodeURIComponent(it.handle) + '.js')
        .then(function(r) { return r.ok ? r.json() : null; })
        .then(function(p) {
          if (!p) return;
          if (!it.title) it.title = p.title || '';
          if (!it.image) it.image = p.featured_image || (p.images && p.images[0]) || '';
          if (!it.type)  it.type  = p.type || p.product_type || '';
          if (!it.url || it.url === '#') it.url = '/products/' + it.handle;
          if (!it.price && p.price != null) {
            it.price = '$' + (parseFloat(p.price) / 100).toFixed(2);
          }
          if (!it.variantId && p.variants && p.variants[0]) {
            it.variantId = p.variants[0].id;
          }
        }).catch(function() {});
    })).then(function() { return items; });
  }

  function cardHtml(item) {
    var title = (item.title || item.handle || 'Untitled').replace(/"/g, '&quot;');
    var url = item.url || (item.handle ? '/products/' + item.handle : '#');
    var img = item.image
      ? '<img src="' + item.image + '" alt="' + title + '" loading="lazy">'
      : '<div class="wishlist-card__img-empty">No image</div>';
    var price = item.price ? '<p class="wishlist-card__price">' + item.price + '</p>' : '';
    var cat = categorize(item.type);
    return ''
      + '<div class="wishlist-card" data-wl-category="' + cat + '">'
      +   '<a href="' + url + '" class="wishlist-card__link">'
      +     '<div class="wishlist-card__img-wrap">' + img + '</div>'
      +     '<div class="wishlist-card__info">'
      +       '<h3 class="wishlist-card__title">' + title + '</h3>'
      +       price
      +     '</div>'
      +   '</a>'
      +   '<button class="wishlist-card__remove" data-remove-handle="'
      +     (item.handle || '') + '" aria-label="Remove from wishlist">&#x2715;</button>'
      + '</div>';
  }

  // Build the category-pill row HTML based on what's actually in the wishlist.
  // Only categories that have ≥1 item are rendered (plus "All", always).
  function pillsHtml(items) {
    var counts = { shirts:0, trousers:0, jackets:0, suits:0, outerwear:0, other:0 };
    items.forEach(function(it){ counts[categorize(it.type)] += 1; });
    var order = ['shirts','trousers','jackets','suits','outerwear','other'];
    var current = window.__lxWishlistFilter || 'all';
    var html = '<div class="lx-cat-pills" data-wl-pills>';
    html += '<div class="lx-cat-pill ' + (current==='all'?'is-active':'') + '" data-wl-pill="all">'
         +    '<span class="lx-cat-pill__icon">' + pillIcon('clipboard') + '</span> All <span class="lx-cat-pill__count">' + items.length + '</span>'
         +  '</div>';
    order.forEach(function(k){
      if (counts[k] === 0) return;
      var meta = CATEGORY_LABELS[k];
      html += '<div class="lx-cat-pill ' + (current===k?'is-active':'') + '" data-wl-pill="' + k + '">'
           +    '<span class="lx-cat-pill__icon">' + pillIcon(meta.icon) + '</span> ' + meta.label
           +    ' <span class="lx-cat-pill__count">' + counts[k] + '</span>'
           +  '</div>';
    });
    html += '</div>';
    return html;
  }

  // Show only cards matching the active pill (or all if 'all').
  function applyFilter(container) {
    var cat = window.__lxWishlistFilter || 'all';
    container.querySelectorAll('.wishlist-card').forEach(function(card){
      var matches = (cat === 'all') || (card.getAttribute('data-wl-category') === cat);
      card.style.display = matches ? '' : 'none';
    });
  }

  function paintGrid(list, container) {
    // Pills go in a sibling container above the grid so they don't get wiped on re-render.
    var pillBox = document.getElementById('wishlist-pills');
    if (!pillBox) {
      pillBox = document.createElement('div');
      pillBox.id = 'wishlist-pills';
      container.parentNode.insertBefore(pillBox, container);
    }
    pillBox.innerHTML = pillsHtml(list);

    pillBox.querySelectorAll('[data-wl-pill]').forEach(function(p){
      p.addEventListener('click', function(){
        window.__lxWishlistFilter = p.getAttribute('data-wl-pill');
        pillBox.querySelectorAll('.lx-cat-pill').forEach(function(x){ x.classList.remove('is-active'); });
        p.classList.add('is-active');
        applyFilter(container);
      });
    });

    container.innerHTML = list.map(cardHtml).join('');
    container.querySelectorAll('[data-remove-handle]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        if (!storageReady()) return;
        var h = btn.getAttribute('data-remove-handle');
        var card = btn.closest('.wishlist-card');
        if (btn.disabled) return;
        btn.disabled = true;

        // Optimistic UI: fade the card immediately, then remove from DOM. The
        // server DELETE fires in the background. This avoids the multi-second
        // wait caused by a full grid rebuild + redundant refresh GET.
        if (card) {
          card.style.transition = 'opacity 0.18s ease, transform 0.18s ease';
          card.style.opacity = '0';
          card.style.transform = 'scale(0.96)';
        }

        Promise.resolve(LuxireStorage.removeFromWishlist(h)).then(function() {
          // Server confirmed. Drop the card from the DOM, refresh pill counts
          // and the header badge from the now-current cache. No grid rebuild,
          // no extra fetches.
          if (card && card.parentNode) card.parentNode.removeChild(card);
          updateAllButtons();
          updateCount();
          recomputePillCounts();
          var totalEl = document.getElementById('wishlist-total');
          if (totalEl) totalEl.textContent = cachedList().length;
          // If we just removed the last item, surface the empty state.
          if (container.querySelectorAll('.wishlist-card').length === 0) {
            var empty = document.getElementById('wishlist-empty');
            if (empty) empty.style.display = '';
            var pillBox = document.getElementById('wishlist-pills');
            if (pillBox) pillBox.innerHTML = '';
          }
        }).catch(function(err) {
          // Roll back — un-fade the card and re-enable the button.
          console.warn('[LuxireWishlist] delete failed', err);
          if (card) {
            card.style.opacity = '';
            card.style.transform = '';
          }
          btn.disabled = false;
        });
      });
    });

    applyFilter(container);
  }

  // Refresh pill counts + visibility based on the cards currently in the DOM.
  // Cheap: just queries the live elements, no network calls.
  function recomputePillCounts() {
    var container = document.getElementById('wishlist-grid');
    var pillBox = document.getElementById('wishlist-pills');
    if (!container || !pillBox) return;

    var counts = { shirts: 0, trousers: 0, jackets: 0, suits: 0, outerwear: 0, other: 0 };
    container.querySelectorAll('.wishlist-card').forEach(function(c) {
      var cat = c.getAttribute('data-wl-category') || 'other';
      if (counts[cat] !== undefined) counts[cat] += 1;
    });
    var total = container.querySelectorAll('.wishlist-card').length;

    pillBox.querySelectorAll('[data-wl-pill]').forEach(function(p) {
      var key = p.getAttribute('data-wl-pill');
      var countEl = p.querySelector('.lx-cat-pill__count');
      if (countEl) countEl.textContent = (key === 'all') ? total : (counts[key] || 0);
      // Hide empty per-category pills, but keep "All" always visible.
      if (key !== 'all') p.style.display = (counts[key] || 0) > 0 ? '' : 'none';
    });
  }

  function renderWishlistGrid() {
    var container = document.getElementById('wishlist-grid');
    if (!container || !storageReady()) return;

    var empty = document.getElementById('wishlist-empty');
    var totalEl = document.getElementById('wishlist-total');
    var pillBox = document.getElementById('wishlist-pills');

    function clearPills(){ if (pillBox) pillBox.innerHTML = ''; }

    // Logged-out users see the empty/sign-in state. We don't auto-redirect
    // here — only clicks on heart buttons should trigger requireLogin.
    if (!isLoggedIn()) {
      container.innerHTML = '';
      clearPills();
      if (empty) empty.style.display = '';
      if (totalEl) totalEl.textContent = '0';
      return;
    }

    // Skeleton while we fetch.
    container.innerHTML = '<div class="wishlist-loading">Loading your wishlist…</div>';
    clearPills();
    if (empty) empty.style.display = 'none';

    LuxireStorage.refreshWishlist().then(function(serverItems) {
      var items = (serverItems || []).map(normalizeItem).filter(Boolean);
      if (totalEl) totalEl.textContent = items.length;
      if (items.length === 0) {
        container.innerHTML = '';
        clearPills();
        if (empty) empty.style.display = '';
        updateCount();
        return;
      }
      if (empty) empty.style.display = 'none';
      enrichMissing(items).then(function(enriched) {
        paintGrid(enriched, container);
        updateCount();
      });
    }).catch(function(e) {
      console.warn('[LuxireWishlist] grid render failed', e);
      container.innerHTML = '<div class="wishlist-error">Couldn’t load your wishlist. Please try again.</div>';
      clearPills();
    });
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  function init() {
    // Event delegation: every heart click in the document is captured here.
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      toggleFromButton(btn);
    });

    // Initial state. For logged-in users, prime the cache from the server first
    // so isWishlisted() returns correct values; for guests, just paint the (empty)
    // initial state so nothing flickers.
    if (storageReady() && isLoggedIn()) {
      Promise.resolve(LuxireStorage.refreshWishlist()).then(function() {
        updateAllButtons();
        updateCount();
        renderWishlistGrid();
      });
    } else {
      updateAllButtons();
      updateCount();
      renderWishlistGrid();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API. Useful for the account page section-switcher to re-render the
  // grid when the user clicks the Wishlist tab.
  window.LuxireWishlist = {
    refresh: function() {
      if (!storageReady()) return Promise.resolve([]);
      return Promise.resolve(LuxireStorage.refreshWishlist()).then(function(list) {
        updateAllButtons();
        updateCount();
        renderWishlistGrid();
        return list;
      });
    },
    isIn: isInWishlist,
    render: renderWishlistGrid,
    toggle: function(handle) {
      var btn = document.querySelector('[data-wishlist-btn][data-wishlist-handle="' + handle + '"]');
      if (btn) toggleFromButton(btn);
    }
  };
})();
